import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { MAU_TOKEN } from '../../chung/components/BieuDo';
import { soNguyen, TRONG } from '../../chung/nghiep-vu/so-thong-ke';
import type { BocToken } from '../../chung/nghiep-vu/types-vet';
import { bocBay, khopPhanHoach } from '../nghiep-vu/vet';

/**
 * Bóc tách bảy loại token của một lượt gọi mô hình (mục 7.3) — port `vet/BocTachToken.tsx` native.
 *
 * Hai điều phải đọc được từ khối này:
 * 1. **`null` khác `0`.** `null` là "nhà cung cấp không báo loại này" (hiện `—`), `0` là "có đo, bằng không".
 * 2. **Bảy loại là một PHÂN HOẠCH của `tong_token`.** Khối này cộng bảy số ra và đặt cạnh tổng server báo, để người
 *    đọc KIỂM được chứ không phải tin. Lệch thì nói lệch bao nhiêu — đó là lỗi bộ chuẩn hoá.
 *
 * Khác native: màu bảy loại lấy từ `MAU_TOKEN` của lớp chung, để một lát "ghi đệm" cùng màu ở biểu đồ Tổng quan và ở
 * đây.
 */

const TRUC = 1000;
const CAO_THANH = 10;

export function BocTachToken({
  token,
  tongToken,
  className,
}: {
  token: BocToken | null | undefined;
  tongToken: number | null;
  className?: string;
}) {
  const { t, locale } = useT();
  const boc = bocBay(token);
  const khop = khopPhanHoach(boc, tongToken);

  const duong = boc.phan.filter((p) => p.giaTri !== null && p.giaTri > 0);
  const nenThanh = duong.reduce((a, p) => a + (p.giaTri ?? 0), 0);

  let chay = 0;

  return (
    <div className={cn('space-y-2', className)}>
      {nenThanh > 0 && (
        <svg
          viewBox={`0 0 ${TRUC} ${CAO_THANH}`}
          preserveAspectRatio="none"
          className="block h-2.5 w-full"
          role="img"
          aria-label={t('nktvSuDungAi.token.thanhNhan')}
        >
          {duong.map((p) => {
            const rong = ((p.giaTri ?? 0) / nenThanh) * TRUC;
            const x = chay;
            chay += rong;
            return (
              <g key={p.loai}>
                <title>{`${t(`nktvSuDungAi.token.${p.loai}`)} — ${soNguyen(p.giaTri, locale)}`}</title>
                <rect x={x} y={0} width={rong} height={CAO_THANH} fill={MAU_TOKEN[p.loai]} />
              </g>
            );
          })}
        </svg>
      )}

      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
        {boc.phan.map((p) => (
          <div key={p.loai} className="flex min-w-0 items-baseline justify-between gap-2">
            <dt className="flex min-w-0 items-center gap-1.5 text-xs text-content-muted">
              <span aria-hidden className="size-2 shrink-0 rounded-sm" style={{ backgroundColor: MAU_TOKEN[p.loai] }} />
              <span className="truncate">{t(`nktvSuDungAi.token.${p.loai}`)}</span>
            </dt>
            <dd
              className={cn(
                'shrink-0 font-mono text-xs tabular-nums',
                p.giaTri === null ? 'text-content-muted' : 'text-content-primary',
              )}
              title={p.giaTri === null ? t('nktvSuDungAi.token.khongBao') : undefined}
            >
              {p.giaTri === null ? TRONG : soNguyen(p.giaTri, locale)}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-default pt-1.5 text-xs">
        <span className="text-content-muted">
          {t('nktvSuDungAi.token.congBay')}{' '}
          <span className="font-mono tabular-nums text-content-primary">
            {boc.tongBay === null ? TRONG : soNguyen(boc.tongBay, locale)}
          </span>
        </span>
        <span className="text-content-muted">
          {t('nktvSuDungAi.token.tongBao')}{' '}
          <span className="font-mono tabular-nums text-content-primary">{soNguyen(tongToken, locale)}</span>
        </span>
        {boc.soKhongBao > 0 && (
          <span className="text-content-muted">
            {t('nktvSuDungAi.token.soKhongBao', { so: soNguyen(boc.soKhongBao, locale) })}
          </span>
        )}
      </div>

      {khop === false && (
        <p className="text-xs text-warning-700" role="status">
          {t('nktvSuDungAi.token.lechPhanHoach', {
            lech: soNguyen(Math.abs((boc.tongBay ?? 0) - (tongToken ?? 0)), locale),
          })}
        </p>
      )}
    </div>
  );
}
